import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import { getAttendance, createAttendance, getEmployees, bulkUploadAttendance, bulkMarkAttendance, downloadAttendanceTemplate, getAttendanceByDateRange, editAttendance } from '../api';
import api from '../api';
import { formatUploadError, downloadUploadErrorsCsv } from '../utils/uploadErrors';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Calendar, Clock, Plus, CheckCircle, Upload, Download, Users, FileSpreadsheet, Save, AlertCircle, Edit3, Search, X, Briefcase, BarChart3, ChevronDown, ChevronRight, Check, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const AttendanceManagement = ({ user, setUser }) => {
  const [attendance, setAttendance] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState('upload'); // 'upload' or 'grid'
  
  // View mode state
  const [viewMode, setViewMode] = useState('records'); // 'records' or 'profession'
  const [professionViewDate, setProfessionViewDate] = useState(new Date().toISOString().split('T')[0]);
  const [expandedProfessions, setExpandedProfessions] = useState({});
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDate, setFilterDate] = useState('');
  
  // Employee select combobox state
  const [employeeSelectOpen, setEmployeeSelectOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    employee_id: '',
    date: new Date().toISOString().split('T')[0],
    status: 'present',
    clock_in: '09:30',
    clock_out: '18:30',
    notes: '',
    leave_type: ''
  });
  
  // Edit attendance states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingAttendance, setEditingAttendance] = useState(null);
  const [editForm, setEditForm] = useState({
    status: '',
    clock_in: '',
    clock_out: '',
    notes: '',
    edit_reason: ''
  });
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Bulk upload states
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  
  // Bulk grid states
  const [gridStartDate, setGridStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [gridEndDate, setGridEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [gridAttendance, setGridAttendance] = useState({});
  const [savingGrid, setSavingGrid] = useState(false);
  const [gridLoaded, setGridLoaded] = useState(false);

  // Permission state: whether user has been granted /attendance access via Permissions module
  const [hasAttendanceAccess, setHasAttendanceAccess] = useState(false);

  useEffect(() => {
    // Fetch user's allowed sections to determine if they can manage attendance
    const fetchPermissions = async () => {
      try {
        const res = await api.get('/permissions/my-sections');
        setHasAttendanceAccess((res.data?.sections || []).includes('/attendance'));
      } catch {
        setHasAttendanceAccess(false);
      }
    };
    fetchPermissions();
  }, []);

  // Check if user can edit attendance — by role OR by explicit permission grant
  const canEditAttendance =
    user?.role === 'admin' ||
    user?.role === 'hr_manager' ||
    user?.role === 'hr_executive' ||
    hasAttendanceAccess;

  const openEditDialog = (record) => {
    const emp = employees.find(e => e.id === record.employee_id);
    setEditingAttendance({
      ...record,
      employee_name: emp ? `${emp.first_name} ${emp.last_name}` : 'Unknown'
    });
    setEditForm({
      status: record.status || '',
      clock_in: record.clock_in || '',
      clock_out: record.clock_out || '',
      notes: record.notes || '',
      edit_reason: ''
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!editForm.edit_reason.trim()) {
      toast.error('Please provide a reason for the edit');
      return;
    }
    
    try {
      setSavingEdit(true);
      await editAttendance(editingAttendance.id, {
        status: editForm.status || null,
        clock_in: editForm.clock_in || null,
        clock_out: editForm.clock_out || null,
        notes: editForm.notes || null,
        edit_reason: editForm.edit_reason
      });
      toast.success('Attendance updated successfully');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update attendance');
    } finally {
      setSavingEdit(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchAttendance();
  }, []);

  const fetchEmployees = async () => {
    try {
      const empRes = await getEmployees();
      const workingEmployees = empRes.data.filter(e => e.employment_status !== 'Exit');
      setEmployees(workingEmployees);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
      toast.error('Failed to load employee list');
    }
  };

  const fetchAttendance = async () => {
    try {
      const attRes = await getAttendance();
      setAttendance(attRes.data);
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    fetchEmployees();
    fetchAttendance();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate against joining date
    const selectedEmp = employees.find(emp => emp.id === formData.employee_id);
    if (selectedEmp?.date_of_joining && formData.date < selectedEmp.date_of_joining) {
      toast.error(`Cannot mark attendance before joining date (${selectedEmp.date_of_joining})`);
      return;
    }
    
    // Validate leave type when on_leave
    if (formData.status === 'on_leave' && !formData.leave_type) {
      toast.error('Please select a leave type');
      return;
    }
    
    try {
      const payload = { ...formData };
      if (payload.status !== 'on_leave') {
        delete payload.leave_type;
      }
      await createAttendance(payload);
      toast.success('Attendance marked successfully');
      setDialogOpen(false);
      fetchData();
      setFormData({
        employee_id: '',
        date: new Date().toISOString().split('T')[0],
        status: 'present',
        clock_in: '09:30',
        clock_out: '18:30',
        notes: '',
        leave_type: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark attendance');
    }
  };

  // Download template
  const handleDownloadTemplate = async () => {
    try {
      const response = await downloadAttendanceTemplate();
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'attendance_template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  // Handle file upload
  const handleFileUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }
    
    setUploading(true);
    setUploadResult(null);
    
    try {
      const response = await bulkUploadAttendance(uploadFile);
      setUploadResult(response.data);
      toast.success(`Upload complete: ${response.data.created} created, ${response.data.updated} updated`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
      setUploadResult({ errors: [error.response?.data?.detail || 'Upload failed'] });
    } finally {
      setUploading(false);
    }
  };

  // Load grid data for date range
  const handleLoadGrid = async () => {
    if (!gridStartDate || !gridEndDate) {
      toast.error('Please select date range');
      return;
    }
    
    if (gridStartDate > gridEndDate) {
      toast.error('Start date must be before end date');
      return;
    }
    
    try {
      const response = await getAttendanceByDateRange(gridStartDate, gridEndDate);
      
      // Build attendance map: { `${employeeId}_${date}`: { status, clock_in, clock_out } }
      const attendanceMap = {};
      response.data.forEach(record => {
        const key = `${record.employee_id}_${record.date}`;
        attendanceMap[key] = {
          status: record.status,
          clock_in: record.clock_in || '',
          clock_out: record.clock_out || ''
        };
      });
      
      setGridAttendance(attendanceMap);
      setGridLoaded(true);
      toast.success('Attendance data loaded');
    } catch (error) {
      toast.error('Failed to load attendance data');
    }
  };

  // Generate date array for grid
  const dateRange = useMemo(() => {
    if (!gridLoaded || !gridStartDate || !gridEndDate) return [];
    
    const dates = [];
    const start = new Date(gridStartDate);
    const end = new Date(gridEndDate);
    
    while (start <= end) {
      dates.push(start.toISOString().split('T')[0]);
      start.setDate(start.getDate() + 1);
    }
    
    return dates;
  }, [gridStartDate, gridEndDate, gridLoaded]);

  // Handle grid cell change
  const handleGridChange = (employeeId, date, field, value) => {
    const key = `${employeeId}_${date}`;
    setGridAttendance(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        status: prev[key]?.status || 'present',
        clock_in: prev[key]?.clock_in || '09:30',
        clock_out: prev[key]?.clock_out || '18:30',
        [field]: value
      }
    }));
  };

  // Save grid attendance
  const handleSaveGrid = async () => {
    const records = [];
    
    Object.entries(gridAttendance).forEach(([key, data]) => {
      if (data.status) {
        const [employeeId, date] = key.split('_');
        records.push({
          employee_id: employeeId,
          date: date,
          status: data.status,
          clock_in: data.clock_in || '',
          clock_out: data.clock_out || '',
          notes: 'Bulk marked by HR'
        });
      }
    });
    
    if (records.length === 0) {
      toast.error('No attendance records to save');
      return;
    }
    
    setSavingGrid(true);
    try {
      const response = await bulkMarkAttendance({ attendance_records: records });
      toast.success(`Saved: ${response.data.created} created, ${response.data.updated} updated`);
      fetchData();
    } catch (error) {
      toast.error('Failed to save attendance');
    } finally {
      setSavingGrid(false);
    }
  };

  // Quick set all employees for a date
  const handleSetAllForDate = (date, status) => {
    const updates = {};
    employees.forEach(emp => {
      const key = `${emp.id}_${date}`;
      updates[key] = {
        ...gridAttendance[key],
        status: status,
        clock_in: status === 'present' || status === 'half_day' ? '09:30' : '',
        clock_out: status === 'present' ? '18:30' : (status === 'half_day' ? '13:30' : '')
      };
    });
    setGridAttendance(prev => ({ ...prev, ...updates }));
    toast.success(`Set all employees to ${status.replace('_', ' ')} for ${date}`);
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp?.name || emp?.employee_id || 'N/A';
  };

  const getEmployeeCode = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp?.employee_id || 'N/A';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present':
        return 'bg-emerald-50 text-emerald-600 border-emerald-200';
      case 'absent':
        return 'bg-rose-50 text-rose-600 border-rose-200';
      case 'half_day':
        return 'bg-amber-50 text-amber-600 border-amber-200';
      case 'on_leave':
        return 'bg-blue-50 text-blue-600 border-blue-200';
      case 'week_off':
        return 'bg-purple-50 text-purple-600 border-purple-200';
      case 'holiday':
        return 'bg-orange-50 text-orange-600 border-orange-200';
      default:
        return 'bg-slate-50 text-slate-600 border-slate-200';
    }
  };

  // Filter attendance records based on search and filters
  const filteredAttendance = useMemo(() => {
    return attendance.filter(record => {
      // Search by employee name or ID
      if (searchQuery) {
        const emp = employees.find(e => e.id === record.employee_id);
        const empName = emp ? `${emp.first_name || ''} ${emp.last_name || ''} ${emp.name || ''} ${emp.employee_id || ''}`.toLowerCase() : '';
        if (!empName.includes(searchQuery.toLowerCase())) {
          return false;
        }
      }
      
      // Filter by status
      if (filterStatus && filterStatus !== 'all' && record.status !== filterStatus) {
        return false;
      }
      
      // Filter by date
      if (filterDate && record.date !== filterDate) {
        return false;
      }
      
      return true;
    });
  }, [attendance, employees, searchQuery, filterStatus, filterDate]);

  // Consolidated Profession View Data
  const professionViewData = useMemo(() => {
    // Filter attendance for selected date
    const dateAttendance = attendance.filter(record => record.date === professionViewDate);
    
    // Group employees by designation/profession
    const professionMap = {};
    
    employees.forEach(emp => {
      const profession = emp.designation || emp.job_title || emp.position || 'Unassigned';
      if (!professionMap[profession]) {
        professionMap[profession] = {
          name: profession,
          employees: [],
          stats: {
            total: 0,
            present: 0,
            absent: 0,
            half_day: 0,
            on_leave: 0,
            week_off: 0,
            holiday: 0,
            not_marked: 0
          }
        };
      }
      
      // Find attendance record for this employee on selected date
      const attendanceRecord = dateAttendance.find(a => a.employee_id === emp.id);
      const status = attendanceRecord?.status || 'not_marked';
      
      professionMap[profession].employees.push({
        ...emp,
        attendance: attendanceRecord,
        status: status
      });
      
      professionMap[profession].stats.total++;
      if (status === 'not_marked') {
        professionMap[profession].stats.not_marked++;
      } else {
        professionMap[profession].stats[status] = (professionMap[profession].stats[status] || 0) + 1;
      }
    });
    
    // Convert to array and sort by total employees
    return Object.values(professionMap).sort((a, b) => b.stats.total - a.stats.total);
  }, [attendance, employees, professionViewDate]);

  // Calculate overall stats for profession view
  const overallProfessionStats = useMemo(() => {
    const stats = {
      total: 0,
      present: 0,
      absent: 0,
      half_day: 0,
      on_leave: 0,
      week_off: 0,
      holiday: 0,
      not_marked: 0
    };
    
    professionViewData.forEach(profession => {
      Object.keys(stats).forEach(key => {
        stats[key] += profession.stats[key];
      });
    });
    
    return stats;
  }, [professionViewData]);

  const toggleProfessionExpand = (professionName) => {
    setExpandedProfessions(prev => ({
      ...prev,
      [professionName]: !prev[professionName]
    }));
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterStatus('all');
    setFilterDate('');
  };

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="attendance-management">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" data-testid="page-title">
              Attendance Management
            </h1>
            <p className="text-slate-600 mt-1">
              Track and manage employee attendance
            </p>
          </div>
          
          {/* Admin/HR/HR Executive Buttons OR users granted /attendance access via Permissions */}
          {canEditAttendance && (
            <div className="flex gap-2">
              {/* Bulk Attendance Button */}
              <Dialog open={bulkDialogOpen} onOpenChange={(open) => {
                setBulkDialogOpen(open);
                if (!open) {
                  setUploadFile(null);
                  setUploadResult(null);
                  setGridLoaded(false);
                  setGridAttendance({});
                }
              }}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="rounded-full" data-testid="bulk-attendance-btn">
                    <Users className="mr-2" size={20} />
                    Bulk Attendance
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Bulk Attendance Management</DialogTitle>
                  </DialogHeader>
                  
                  <Tabs value={bulkMode} onValueChange={setBulkMode} className="mt-4">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="upload" className="flex items-center gap-2">
                        <Upload size={16} />
                        Excel Upload
                      </TabsTrigger>
                      <TabsTrigger value="grid" className="flex items-center gap-2">
                        <FileSpreadsheet size={16} />
                        On-Screen Grid
                      </TabsTrigger>
                    </TabsList>
                    
                    {/* Excel Upload Tab */}
                    <TabsContent value="upload" className="space-y-4 mt-4">
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">Upload Attendance Excel</CardTitle>
                          <CardDescription>
                            Upload an Excel file with attendance data for multiple employees and dates
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <Button 
                            variant="outline" 
                            onClick={handleDownloadTemplate}
                            className="w-full"
                            data-testid="download-template-btn"
                          >
                            <Download className="mr-2" size={16} />
                            Download Template
                          </Button>
                          
                          <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                            <input
                              type="file"
                              accept=".xlsx,.xls"
                              onChange={(e) => setUploadFile(e.target.files[0])}
                              className="hidden"
                              id="attendance-file-upload"
                              data-testid="file-input"
                            />
                            <label 
                              htmlFor="attendance-file-upload" 
                              className="cursor-pointer"
                            >
                              <Upload className="mx-auto mb-2 text-slate-400" size={32} />
                              <p className="text-sm text-slate-600">
                                {uploadFile ? uploadFile.name : 'Click to select Excel file (.xlsx, .xls)'}
                              </p>
                            </label>
                          </div>
                          
                          <Button 
                            onClick={handleFileUpload} 
                            disabled={!uploadFile || uploading}
                            className="w-full bg-indigo-600 hover:bg-indigo-700"
                            data-testid="upload-btn"
                          >
                            {uploading ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Uploading...
                              </>
                            ) : (
                              <>
                                <Upload className="mr-2" size={16} />
                                Upload File
                              </>
                            )}
                          </Button>
                          
                          {uploadResult && (
                            <div className={`p-4 rounded-lg ${uploadResult.errors?.length > 0 && uploadResult.total_processed === 0 ? 'bg-rose-50' : 'bg-emerald-50'}`}>
                              {uploadResult.total_processed > 0 && (
                                <div className="text-emerald-700 mb-2">
                                  <p className="font-medium">Upload Successful!</p>
                                  <p className="text-sm">Created: {uploadResult.created}, Updated: {uploadResult.updated}</p>
                                </div>
                              )}
                              {uploadResult.errors?.length > 0 && (
                                <div className="text-rose-700">
                                  <div className="flex items-center justify-between gap-2 mb-1">
                                    <p className="font-medium flex items-center gap-1">
                                      <AlertCircle size={16} />
                                      {uploadResult.errors.length} Error(s):
                                    </p>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="h-7 text-xs"
                                      onClick={() => downloadUploadErrorsCsv(uploadResult.errors, 'attendance-upload-errors.csv')}
                                      data-testid="download-upload-errors-csv"
                                    >
                                      Download errors (CSV)
                                    </Button>
                                  </div>
                                  <ul className="text-sm list-disc list-inside max-h-32 overflow-y-auto">
                                    {uploadResult.errors.map((err, i) => (
                                      <li key={i}>{formatUploadError(err)}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>
                    
                    {/* On-Screen Grid Tab */}
                    <TabsContent value="grid" className="space-y-4 mt-4">
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">Mark Attendance Grid</CardTitle>
                          <CardDescription>
                            Select date range and mark attendance for all employees on screen
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                              <Label>Start Date</Label>
                              <Input
                                type="date"
                                value={gridStartDate}
                                onChange={(e) => {
                                  setGridStartDate(e.target.value);
                                  setGridLoaded(false);
                                }}
                                data-testid="grid-start-date"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>End Date</Label>
                              <Input
                                type="date"
                                value={gridEndDate}
                                onChange={(e) => {
                                  setGridEndDate(e.target.value);
                                  setGridLoaded(false);
                                }}
                                data-testid="grid-end-date"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>&nbsp;</Label>
                              <Button 
                                onClick={handleLoadGrid}
                                className="w-full"
                                variant="outline"
                                data-testid="load-grid-btn"
                              >
                                Load Grid
                              </Button>
                            </div>
                          </div>
                          
                          {gridLoaded && dateRange.length > 0 && (
                            <>
                              <div className="overflow-x-auto border rounded-lg">
                                <table className="w-full text-sm">
                                  <thead className="bg-slate-50">
                                    <tr>
                                      <th className="text-left p-3 font-medium text-slate-700 sticky left-0 bg-slate-50 min-w-[150px]">
                                        Employee
                                      </th>
                                      {dateRange.map(date => (
                                        <th key={date} className="p-2 font-medium text-slate-700 min-w-[120px]">
                                          <div className="text-xs">{new Date(date).toLocaleDateString('en-US', { weekday: 'short' })}</div>
                                          <div>{date.split('-').slice(1).join('/')}</div>
                                          <div className="flex gap-1 mt-1 justify-center flex-wrap">
                                            <button 
                                              className="text-[10px] px-1 py-0.5 bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200"
                                              onClick={() => handleSetAllForDate(date, 'present')}
                                            >
                                              All P
                                            </button>
                                            <button 
                                              className="text-[10px] px-1 py-0.5 bg-rose-100 text-rose-700 rounded hover:bg-rose-200"
                                              onClick={() => handleSetAllForDate(date, 'absent')}
                                            >
                                              All A
                                            </button>
                                            <button 
                                              className="text-[10px] px-1 py-0.5 bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                                              onClick={() => handleSetAllForDate(date, 'week_off')}
                                            >
                                              All WO
                                            </button>
                                            <button 
                                              className="text-[10px] px-1 py-0.5 bg-orange-100 text-orange-700 rounded hover:bg-orange-200"
                                              onClick={() => handleSetAllForDate(date, 'holiday')}
                                            >
                                              All H
                                            </button>
                                          </div>
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {employees.map(emp => (
                                      <tr key={emp.id} className="border-t hover:bg-slate-50">
                                        <td className="p-3 sticky left-0 bg-white">
                                          <div className="font-medium text-slate-900">{emp.name || emp.employee_id}</div>
                                          <div className="text-xs text-slate-500">{emp.employee_id}</div>
                                        </td>
                                        {dateRange.map(date => {
                                          const key = `${emp.id}_${date}`;
                                          const cellData = gridAttendance[key] || { status: '', clock_in: '', clock_out: '' };
                                          
                                          return (
                                            <td key={date} className="p-2 border-l">
                                              <Select
                                                value={cellData.status || ''}
                                                onValueChange={(value) => handleGridChange(emp.id, date, 'status', value)}
                                              >
                                                <SelectTrigger className="h-8 text-xs">
                                                  <SelectValue placeholder="-" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                  <SelectItem value="present">Present</SelectItem>
                                                  <SelectItem value="absent">Absent</SelectItem>
                                                  <SelectItem value="half_day">Half Day</SelectItem>
                                                  <SelectItem value="on_leave">On Leave</SelectItem>
                                                  <SelectItem value="week_off">Week Off</SelectItem>
                                                  <SelectItem value="holiday">Holiday</SelectItem>
                                                </SelectContent>
                                              </Select>
                                              {cellData.status === 'present' || cellData.status === 'half_day' ? (
                                                <div className="flex gap-1 mt-1">
                                                  <Input
                                                    type="time"
                                                    value={cellData.clock_in}
                                                    onChange={(e) => handleGridChange(emp.id, date, 'clock_in', e.target.value)}
                                                    className="h-6 text-[10px] px-1"
                                                    placeholder="In"
                                                  />
                                                  <Input
                                                    type="time"
                                                    value={cellData.clock_out}
                                                    onChange={(e) => handleGridChange(emp.id, date, 'clock_out', e.target.value)}
                                                    className="h-6 text-[10px] px-1"
                                                    placeholder="Out"
                                                  />
                                                </div>
                                              ) : null}
                                            </td>
                                          );
                                        })}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              
                              <Button 
                                onClick={handleSaveGrid}
                                disabled={savingGrid}
                                className="w-full bg-emerald-600 hover:bg-emerald-700"
                                data-testid="save-grid-btn"
                              >
                                {savingGrid ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Saving...
                                  </>
                                ) : (
                                  <>
                                    <Save className="mr-2" size={16} />
                                    Save All Attendance
                                  </>
                                )}
                              </Button>
                            </>
                          )}
                          
                          {gridLoaded && dateRange.length === 0 && (
                            <p className="text-center text-slate-500 py-4">No dates in selected range</p>
                          )}
                          
                          {!gridLoaded && (
                            <p className="text-center text-slate-500 py-4">
                              Select date range and click "Load Grid" to start marking attendance
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>
                  </Tabs>
                </DialogContent>
              </Dialog>
              
              {/* Single Mark Attendance Button */}
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full" data-testid="mark-attendance-btn">
                    <Plus className="mr-2" size={20} />
                    Mark Attendance
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="mark-attendance-dialog">
                  <DialogHeader>
                    <DialogTitle>Mark Attendance</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="attendance-form">
                    <div className="space-y-2">
                      <Label htmlFor="employee_id">Employee *</Label>
                      <Popover open={employeeSelectOpen} onOpenChange={setEmployeeSelectOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={employeeSelectOpen}
                            className="w-full justify-between font-normal"
                            data-testid="employee-select"
                          >
                            {formData.employee_id
                              ? (() => {
                                  const emp = employees.find(e => e.id === formData.employee_id);
                                  return emp ? `${emp.name || emp.employee_id}` : "Select employee";
                                })()
                              : "Select employee"}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[400px] p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Search employee by name or ID..." />
                            <CommandList>
                              <CommandEmpty>No employee found.</CommandEmpty>
                              <CommandGroup>
                                {employees.map(emp => (
                                  <CommandItem
                                    key={emp.id}
                                    value={`${emp.name || ''} ${emp.employee_id || ''}`}
                                    onSelect={() => {
                                      setFormData({ ...formData, employee_id: emp.id });
                                      setEmployeeSelectOpen(false);
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        formData.employee_id === emp.id ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                    <div className="flex flex-col">
                                      <span>{emp.name || emp.employee_id}</span>
                                      <span className="text-xs text-slate-400">
                                        {emp.employee_id} {emp.date_of_joining && `• Joined: ${emp.date_of_joining}`}
                                      </span>
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      {formData.employee_id && (() => {
                        const selectedEmp = employees.find(e => e.id === formData.employee_id);
                        if (selectedEmp?.date_of_joining) {
                          return (
                            <p className="text-xs text-slate-500 mt-1">
                              <Calendar size={12} className="inline mr-1" />
                              Joining Date: {selectedEmp.date_of_joining}
                            </p>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="date">Date *</Label>
                      <Input
                        id="date"
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        min={(() => {
                          const selectedEmp = employees.find(e => e.id === formData.employee_id);
                          return selectedEmp?.date_of_joining || undefined;
                        })()}
                        required
                        data-testid="date-input"
                      />
                      {formData.employee_id && formData.date && (() => {
                        const selectedEmp = employees.find(e => e.id === formData.employee_id);
                        if (selectedEmp?.date_of_joining && formData.date < selectedEmp.date_of_joining) {
                          return (
                            <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                              <AlertCircle size={12} />
                              Cannot mark attendance before joining date ({selectedEmp.date_of_joining})
                            </p>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="status">Status *</Label>
                      <Select
                        value={formData.status}
                        onValueChange={(value) => setFormData({ ...formData, status: value, leave_type: value === 'on_leave' ? formData.leave_type : '' })}
                        required
                      >
                        <SelectTrigger data-testid="status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="present">Present</SelectItem>
                          <SelectItem value="absent">Absent</SelectItem>
                          <SelectItem value="half_day">Half Day</SelectItem>
                          <SelectItem value="on_leave">On Leave</SelectItem>
                          <SelectItem value="week_off">Week Off</SelectItem>
                          <SelectItem value="holiday">Holiday</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {formData.status === 'on_leave' && (
                      <div className="space-y-2">
                        <Label htmlFor="leave_type">Leave Type *</Label>
                        <Select
                          value={formData.leave_type}
                          onValueChange={(value) => setFormData({ ...formData, leave_type: value })}
                          required
                        >
                          <SelectTrigger data-testid="leave-type-select">
                            <SelectValue placeholder="Select leave type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sick">Sick Leave</SelectItem>
                            <SelectItem value="casual">Casual Leave</SelectItem>
                            <SelectItem value="annual">Annual Leave</SelectItem>
                            <SelectItem value="unpaid">Unpaid Leave</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-amber-600">This will auto-create an approved leave record in Leave Management</p>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="clock_in">Clock In</Label>
                        <Input
                          id="clock_in"
                          type="time"
                          value={formData.clock_in}
                          onChange={(e) => setFormData({ ...formData, clock_in: e.target.value })}
                          data-testid="clock-in-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="clock_out">Clock Out</Label>
                        <Input
                          id="clock_out"
                          type="time"
                          value={formData.clock_out}
                          onChange={(e) => setFormData({ ...formData, clock_out: e.target.value })}
                          data-testid="clock-out-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="notes">Notes</Label>
                      <Input
                        id="notes"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        data-testid="notes-input"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 rounded-full" data-testid="submit-attendance-btn">
                      Mark Attendance
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>

        {/* View Mode Tabs */}
        <Tabs value={viewMode} onValueChange={setViewMode} className="w-full">
          <TabsList className="grid w-full grid-cols-2 max-w-md mb-4">
            <TabsTrigger value="records" className="flex items-center gap-2">
              <Users size={16} />
              Records View
            </TabsTrigger>
            <TabsTrigger value="profession" className="flex items-center gap-2">
              <Briefcase size={16} />
              Profession View
            </TabsTrigger>
          </TabsList>

          {/* Records View Tab */}
          <TabsContent value="records">
            <Card className="border border-slate-100 shadow-sm">
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <CardTitle>Attendance Records</CardTitle>
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Search Input */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                      <Input
                        type="text"
                        placeholder="Search employee..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 w-48"
                        data-testid="employee-search-input"
                      />
                    </div>
                    
                    {/* Status Filter */}
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger className="w-32" data-testid="status-filter">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="present">Present</SelectItem>
                        <SelectItem value="absent">Absent</SelectItem>
                        <SelectItem value="half_day">Half Day</SelectItem>
                        <SelectItem value="on_leave">On Leave</SelectItem>
                        <SelectItem value="week_off">Week Off</SelectItem>
                        <SelectItem value="holiday">Holiday</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {/* Date Filter */}
                    <Input
                      type="date"
                      value={filterDate}
                      onChange={(e) => setFilterDate(e.target.value)}
                      className="w-36"
                      data-testid="date-filter"
                    />
                    
                    {/* Clear Filters */}
                    {(searchQuery || filterStatus !== 'all' || filterDate) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearFilters}
                        className="text-slate-500 hover:text-slate-700"
                        data-testid="clear-filters-btn"
                      >
                        <X size={16} className="mr-1" />
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
                {filteredAttendance.length !== attendance.length && (
                  <p className="text-sm text-slate-500 mt-2">
                    Showing {filteredAttendance.length} of {attendance.length} records
                  </p>
                )}
              </CardHeader>
              <CardContent>
                {filteredAttendance.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">
                    {attendance.length === 0 ? 'No attendance records found' : 'No records match your search'}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {filteredAttendance.slice(0, 50).map((record, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors" data-testid={`attendance-record-${index}`}>
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center font-bold mono">
                            {getEmployeeName(record.employee_id).substring(0, 2)}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900" data-testid={`employee-name-${index}`}>{getEmployeeName(record.employee_id)}</p>
                            <p className="text-sm text-slate-600 flex items-center gap-2">
                              <Calendar size={14} />
                              {record.date}
                              {record.clock_in && (
                                <>
                                  <Clock size={14} />
                                  {record.clock_in} - {record.clock_out || 'N/A'}
                                </>
                              )}
                            </p>
                            {record.marked_by_name && (
                              <p className="text-xs text-slate-400 mt-1">
                                Marked by {record.marked_by_name} on {record.created_at ? new Date(record.created_at).toLocaleString() : 'N/A'}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className={`px-4 py-1 rounded-full text-xs font-medium border ${getStatusColor(record.status)}`} data-testid={`status-badge-${index}`}>
                            {record.status.replace('_', ' ').toUpperCase()}
                          </div>
                          {canEditAttendance && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(record)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Edit3 size={16} />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profession View Tab */}
          <TabsContent value="profession">
            <Card className="border border-slate-100 shadow-sm">
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Briefcase size={20} />
                      Consolidated Profession View
                    </CardTitle>
                    <CardDescription className="mt-1">
                      Attendance grouped by designation/profession
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-3">
                    <Label className="text-sm text-slate-600">Select Date:</Label>
                    <Input
                      type="date"
                      value={professionViewDate}
                      onChange={(e) => setProfessionViewDate(e.target.value)}
                      className="w-40"
                      data-testid="profession-date-filter"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Overall Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
                  <div className="bg-slate-50 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-slate-700">{overallProfessionStats.total}</p>
                    <p className="text-xs text-slate-500">Total</p>
                  </div>
                  <div className="bg-emerald-50 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-emerald-600">{overallProfessionStats.present}</p>
                    <p className="text-xs text-emerald-600">Present</p>
                  </div>
                  <div className="bg-rose-50 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-rose-600">{overallProfessionStats.absent}</p>
                    <p className="text-xs text-rose-600">Absent</p>
                  </div>
                  <div className="bg-amber-50 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-amber-600">{overallProfessionStats.half_day}</p>
                    <p className="text-xs text-amber-600">Half Day</p>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-600">{overallProfessionStats.on_leave}</p>
                    <p className="text-xs text-blue-600">On Leave</p>
                  </div>
                  <div className="bg-purple-50 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-purple-600">{overallProfessionStats.week_off}</p>
                    <p className="text-xs text-purple-600">Week Off</p>
                  </div>
                  <div className="bg-orange-50 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-orange-600">{overallProfessionStats.holiday}</p>
                    <p className="text-xs text-orange-600">Holiday</p>
                  </div>
                  <div className="bg-gray-100 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-gray-600">{overallProfessionStats.not_marked}</p>
                    <p className="text-xs text-gray-600">Not Marked</p>
                  </div>
                </div>

                {/* Profession-wise Breakdown */}
                {professionViewData.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No employees found</p>
                ) : (
                  <div className="space-y-3">
                    {professionViewData.map((profession, idx) => (
                      <div key={idx} className="border border-slate-200 rounded-lg overflow-hidden">
                        {/* Profession Header */}
                        <div 
                          className="flex items-center justify-between p-4 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                          onClick={() => toggleProfessionExpand(profession.name)}
                          data-testid={`profession-row-${idx}`}
                        >
                          <div className="flex items-center gap-3">
                            {expandedProfessions[profession.name] ? (
                              <ChevronDown size={20} className="text-slate-500" />
                            ) : (
                              <ChevronRight size={20} className="text-slate-500" />
                            )}
                            <div className="w-10 h-10 bg-indigo-100 text-indigo-600 rounded-lg flex items-center justify-center">
                              <Briefcase size={18} />
                            </div>
                            <div>
                              <p className="font-semibold text-slate-800">{profession.name}</p>
                              <p className="text-xs text-slate-500">{profession.stats.total} employees</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="px-2 py-1 text-xs font-medium bg-emerald-100 text-emerald-700 rounded">
                              P: {profession.stats.present}
                            </span>
                            <span className="px-2 py-1 text-xs font-medium bg-rose-100 text-rose-700 rounded">
                              A: {profession.stats.absent}
                            </span>
                            <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded">
                              HD: {profession.stats.half_day}
                            </span>
                            <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                              L: {profession.stats.on_leave}
                            </span>
                            {profession.stats.week_off > 0 && (
                              <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-700 rounded">
                                WO: {profession.stats.week_off}
                              </span>
                            )}
                            {profession.stats.holiday > 0 && (
                              <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-700 rounded">
                                H: {profession.stats.holiday}
                              </span>
                            )}
                            {profession.stats.not_marked > 0 && (
                              <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                                NM: {profession.stats.not_marked}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Expanded Employee List */}
                        {expandedProfessions[profession.name] && (
                          <div className="border-t border-slate-200">
                            <div className="divide-y divide-slate-100">
                              {profession.employees.map((emp, empIdx) => (
                                <div key={empIdx} className="flex items-center justify-between px-4 py-3 pl-12 hover:bg-slate-50">
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-slate-200 text-slate-600 rounded-full flex items-center justify-center text-sm font-medium">
                                      {(emp.first_name || emp.name || 'U')[0]}
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium text-slate-700">
                                        {emp.first_name && emp.last_name ? `${emp.first_name} ${emp.last_name}` : emp.name || emp.employee_id}
                                      </p>
                                      <p className="text-xs text-slate-500">{emp.employee_id}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {emp.attendance ? (
                                      <>
                                        <div className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(emp.status)}`}>
                                          {emp.status.replace('_', ' ').toUpperCase()}
                                        </div>
                                        {emp.attendance.clock_in && (
                                          <span className="text-xs text-slate-500">
                                            {emp.attendance.clock_in} - {emp.attendance.clock_out || 'N/A'}
                                          </span>
                                        )}
                                      </>
                                    ) : (
                                      <div className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                                        NOT MARKED
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Edit Attendance Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="h-5 w-5" />
              Edit Attendance
            </DialogTitle>
          </DialogHeader>
          {editingAttendance && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium">{editingAttendance.employee_name}</p>
                <p className="text-sm text-gray-500">Date: {editingAttendance.date}</p>
              </div>
              
              <div className="space-y-3">
                <div>
                  <Label>Status</Label>
                  <Select value={editForm.status} onValueChange={(v) => setEditForm(prev => ({ ...prev, status: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="present">Present</SelectItem>
                      <SelectItem value="absent">Absent</SelectItem>
                      <SelectItem value="half_day">Half Day</SelectItem>
                      <SelectItem value="on_leave">On Leave</SelectItem>
                      <SelectItem value="holiday">Holiday</SelectItem>
                      <SelectItem value="week_off">Week Off</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Clock In</Label>
                    <Input
                      type="time"
                      value={editForm.clock_in}
                      onChange={(e) => setEditForm(prev => ({ ...prev, clock_in: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Clock Out</Label>
                    <Input
                      type="time"
                      value={editForm.clock_out}
                      onChange={(e) => setEditForm(prev => ({ ...prev, clock_out: e.target.value }))}
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Notes</Label>
                  <Input
                    value={editForm.notes}
                    onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Optional notes"
                  />
                </div>
                
                <div>
                  <Label className="text-red-600">Reason for Edit *</Label>
                  <Textarea
                    value={editForm.edit_reason}
                    onChange={(e) => setEditForm(prev => ({ ...prev, edit_reason: e.target.value }))}
                    placeholder="Please provide a reason for this edit (required)"
                    rows={3}
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleEditSubmit} disabled={savingEdit}>
                  {savingEdit ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AttendanceManagement;
